import { getInsuranceDocMap, zClientInsuranceDocUploads } from '@/general/src/insuranceDoc/shared.js'
import { zDateString } from '@/general/src/other/date.js'
import { zGosNumberRuRequired } from '@/general/src/other/validation.js'
import { z } from 'zod'

const stsCsvSrc = `Название поля в программе,alias,Название поля на фотографии в документе,Подсказка для ИИ,Валидация,Пример заполнения
Гос. номер автомобиля,registrationNumber,Государственный регистрационный номер,"Буквы: А, В, Е, К, М, Н, О, Р, С, Т, У, Х и цифры",обязательно,У266XX163
Идентификационный номер (VIN),vin,Идентификационный номер (VIN),"17 символов, цифры и латинские буквы",обязательно,LB37852D6PS548422
Марка ТС,brand,Марка,"марка автомобиля, часто пишется вместе с моделью автомобиля, но находится перед ней",обязательно,GEELY
Модель ТС,model,Модель,"модель автомобиля, часто пишется вместе с маркой автомобиля, но находится после нее",обязательно,MONJARO
Тип ТС,type,Тип ТС,"описание типа транспортного средства, например ""легковой универсал"" или ""грузовой""",опционально,Легковой универсал
Категория ТС,category,"Категория ТС (ABCD, прицеп)","Аналогично категории прав. Буква или буква с цифрой, бывают: A, B, C, D, E, A1, B1, C1, D1, BE, CE, DE, C1E, D1E, M, Tm, Tb.",опционально,"B, M1G"
Год выпуска,year,Год выпуска,год выпуска транспортного средства,"обязательно, DDDD",2023
Мощность двигателя (кВТ),powerKw,Мощность двигателя (кВТ),"В документе может называться просто мощность, но если есть приписка кВТ, то это оно",опционально,175
Мощность двигателя (л.с.),powerHp,Мощность двигателя (лс),"В документе может называться просто мощность, но если есть приписка лс, то это оно. Если указано только лс или только кВТ. Высчитай недостающее поле сам исходя из того что 1 лс = 0,7355 кВТ",обязательно,238
Серия,series,Серия,"цифры и буквы, всегда 4 символа","обязательно, DD DD",9067
Номер,number,Номер,"цифры, оставшиеся цифры после серии","обязательно, DDDDDD ",716329
Цвет ТС,color,Цвет,"цвет транспортного средства, например ""зеленый""",опционально ,ЗЕЛЕНЫЙ
Фамилия собственника,ownerLastName,Собственик (владелец),В первой строчке написана фамилия русскими буквами,обязательно,Мошненко
Имя собственника,ownerFirstName,Собственик (владелец),"На следующей строчке после фамилии, написано имя русскими буквами",обязательно,Петр
Отчество собственника,ownerMiddleName,Собственик (владелец),"Может не быть, но если есть будет написано после имени русскими буквами",опционально,Владимирович
Республика/край/область,ownerRegion,Субъект Российской Федерации,{{region}},обязательно,Самарская область
Район,ownerDistrict,Район,"район области или города, например ""Алексеевский район"", может не быть",опционально,Октябрьский район
Населённый пункт,ownerCity,Населённый пункт,{{city}},обязательно,г. Самара
Улица,ownerStreet,Улица,{{street}},обязательно,ул. Челюскинцев
Дом,ownerHouse,Дом,"номер дома, цифра, может быть с буквой, например 10А",обязательно,76
Корпус (строение),ownerBuilding,Корпус/строение,Может быть строение (стр.) или корпус (к.),опционально,стр. 2
Квартира,ownerApartment,Квартира,"Номер квартиры, может не быть",опционально,55
Код подразделения ГИБДД,ownerDepartmentCode,Код подразделения ГИБДД,цифры и буквы,обязательно,1136099
Дата выдачи,issuedDate,Дата выдачи,Дата выдачи свидетельства транспортного средства,"обязательно, дата",20.10.2023`

export const zSts = z.object({
  registrationNumber: zGosNumberRuRequired,
  vin: z.string().min(1),
  brand: z.string().min(1),
  model: z.string().min(1),
  type: z.string().optional(),
  category: z.string().optional(),
  year: z.string().min(1),
  powerKw: z.string().optional(),
  powerHp: z.string().min(1),
  series: z.string().min(1),
  number: z.string().min(1),
  color: z.string().optional(),
  ownerLastName: z.string().min(1),
  ownerFirstName: z.string().min(1),
  ownerMiddleName: z.string().optional(),
  ownerRegion: z.string().min(1),
  ownerDistrict: z.string().optional(),
  ownerCity: z.string().min(1),
  ownerStreet: z.string().min(1),
  ownerHouse: z.string().min(1),
  ownerBuilding: z.string().optional(),
  ownerApartment: z.string().optional(),
  // ownerDepartmentCode: z.string().min(1),
  ownerDepartmentCode: z.string().optional(),
  issuedDate: zDateString,
})
export type StsWithoutUploads = z.output<typeof zSts>
export const zStsWithUploads = z.object({
  insuranceDocsUploads: zClientInsuranceDocUploads,
  ...zSts.shape,
})

export const stsFieldsMap = getInsuranceDocMap({
  csv: stsCsvSrc,
  zSchema: zStsWithUploads,
  extra: {
    insuranceDocsUploads: { label: 'Фотография свидетельства о регистрации транспортного средства (лицевая сторона)' },
  },
})
export const getStsFieldTitle = (field: keyof typeof stsFieldsMap) => stsFieldsMap[field].label
export const getStsFieldHint = (field: keyof typeof stsFieldsMap) => stsFieldsMap[field].hint

export const stsInitialValues: z.infer<typeof zStsWithUploads> = {
  insuranceDocsUploads: [],
  registrationNumber: '',
  vin: '',
  brand: '',
  model: '',
  year: '',
  powerKw: '',
  powerHp: '',
  series: '',
  number: '',
  color: '',
  ownerLastName: '',
  ownerFirstName: '',
  ownerMiddleName: '',
  ownerRegion: '',
  ownerDistrict: '',
  ownerCity: '',
  ownerStreet: '',
  ownerHouse: '',
  ownerApartment: '',
  ownerDepartmentCode: '',
  issuedDate: '',
}

// export const stsExampleValues: z.infer<typeof zStsWithUploads> = {
//   insuranceDocsUploads: [],
//   registrationNumber: 'А123АА123456',
//   vin: '12345678901234567',
//   brand: 'Toyota',
//   model: 'Camry',
//   year: '2020',
//   powerKw: '150',
//   powerHp: '200',
//   series: '1234',
//   number: '123456',
//   color: 'Красный',
//   ownerLastName: 'Иванов',
//   ownerFirstName: 'Иван',
//   ownerMiddleName: 'Иванович',
//   ownerRegion: 'Москва',
//   ownerDistrict: 'Центральный',
//   ownerCity: 'Москва',
//   ownerStreet: 'Ленина',
//   ownerHouse: '1',
//   ownerApartment: '1',
//   ownerDepartmentCode: '123456',
//   issuedDate: '01.01.2020',
// }

// export const stsExampleValues: z.infer<typeof zStsWithUploads> = {
//   insuranceDocsUploads: [],
//   registrationNumber: 'У266ХХ163',
//   vin: 'LB37852D6PS548422',
//   brand: 'GEELY',
//   model: 'MONJARO',
//   // brand: 'ZXC',
//   // model: 'BVC',
//   year: '2023',
//   powerKw: '175',
//   powerHp: '238',
//   series: '9067',
//   number: '716329',
//   color: 'ЗЕЛЕНЫЙ',
//   ownerLastName: 'МОШНЕНКО',
//   ownerFirstName: 'ПЕТР',
//   ownerMiddleName: 'ВЛАДИМИРОВИЧ',
//   ownerRegion: 'Самарская область',
//   ownerDistrict: 'Октябрьский район',
//   ownerCity: 'г. Самара',
//   ownerStreet: 'ул. Челюскинцев',
//   ownerHouse: '76',
//   ownerApartment: '55',
//   ownerDepartmentCode: '1136099',
//   issuedDate: '20.10.2023',
// }

export const stsExampleValues: z.infer<typeof zStsWithUploads> = {
  insuranceDocsUploads: [],
  registrationNumber: 'У940ВМ550',
  vin: 'XWEPC811DD0040464',
  brand: 'KIA',
  model: 'SPORTAGE',
  year: '2013',
  powerKw: '175',
  powerHp: '238',
  // model: 'Cerato',
  // year: '2023',
  // powerKw: '150',
  // powerHp: '200',
  series: '9972',
  number: '971398',
  color: 'ЧЕРНЫЙ',
  ownerLastName: 'ЛУЗГИН',
  ownerFirstName: 'ИЛЬЯ',
  ownerMiddleName: 'АНДРЕЕВИЧ',
  ownerRegion: 'Московская область',
  ownerDistrict: '',
  ownerCity: 'д. Камовка',
  ownerStreet: 'ул. Камова',
  ownerHouse: '5',
  ownerApartment: '6',
  ownerDepartmentCode: '114-522',
  issuedDate: '09.04.2025',
  type: 'Легковой универсал',
  category: 'B',
  ownerBuilding: '',
}
